import { useState } from "react";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Loader, EmptyState } from "@/components/common/Loader";
import { TicketStatusBadge, PriorityBadge, SlaBadge } from "@/components/common/Badges";
import { useFetch } from "@/lib/api";
import { TICKET_STATUS, OPEN_STATUSES, fmtDate } from "@/lib/format";

function Filter({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-ivory-deep rounded-xl h-10 text-sm"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function AdminTickets() {
  const [f, setF] = useState({ status: "all", category_id: "all", center_id: "all", client_type_id: "all", assignee_id: "all" });
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "all"))).toString();
  const { data, loading } = useFetch(`/admin/tickets?${qs}`);
  const { data: lists } = useFetch("/admin/lists");
  const { data: centers } = useFetch("/admin/centers");
  const { data: staff } = useFetch("/admin/staff-options");
  const set = (k) => (v) => setF({ ...f, [k]: v });

  return (
    <div>
      <PageHeader eyebrow="Assistenza" title="Coda ticket" subtitle="In cima le richieste aperte da più tempo." />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="ticket-filters">
        <Filter testid="filter-status" value={f.status} onChange={set("status")} placeholder="Tutti gli stati" options={Object.entries(TICKET_STATUS).map(([id, name]) => ({ id, name }))} />
        <Filter testid="filter-category" value={f.category_id} onChange={set("category_id")} placeholder="Tutte le categorie" options={lists?.["ticket-categories"] || []} />
        <Filter testid="filter-center" value={f.center_id} onChange={set("center_id")} placeholder="Tutti i centri" options={(centers || []).map((c) => ({ id: c.id, name: c.center_name }))} />
        <Filter testid="filter-client-type" value={f.client_type_id} onChange={set("client_type_id")} placeholder="Tutte le tipologie" options={lists?.["client-types"] || []} />
        <Filter testid="filter-assignee" value={f.assignee_id} onChange={set("assignee_id")} placeholder="Tutti gli assegnatari" options={[{ id: "none", name: "Non assegnati" }, ...(staff || [])]} />
      </div>
      {loading && !data ? <Loader /> : !data?.length ? <EmptyState title="Nessun ticket" text="Nessuna richiesta corrisponde ai filtri." /> : (
        <div className="lux-card divide-y divide-warm/30" data-testid="admin-ticket-list">
          {data.map((t) => {
            const open = OPEN_STATUSES.includes(t.status);
            return (
              <Link key={t.id} to={`/admin/ticket/${t.id}`} data-testid={`admin-ticket-row-${t.code}`} className="block px-5 py-4 hover:bg-ivory-deep/60 transition-colors">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="text-xs text-warm-text tracking-[0.1em]">{t.code}</span>
                  <TicketStatusBadge status={t.status} />
                  <PriorityBadge priority={t.priority} />
                  <span className="text-xs text-warm-text ml-auto">{fmtDate(t.created_at)}</span>
                </div>
                <p className="font-serif text-lg mt-1 truncate">{t.subject}</p>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-1 text-xs text-warm-text">
                  <span>{t.center_name}</span><span>{t.category_name}</span><span>{t.assignee_name ? `→ ${t.assignee_name}` : "Non assegnato"}</span>
                  {open && <SlaBadge label="Aperto da" since={t.created_at} testid={`sla-open-${t.code}`} />}
                  {open && <SlaBadge label={t.last_message_by === "center" ? "Il centro attende da" : "Ultima risposta"} since={t.last_message_at} testid={`sla-last-${t.code}`} />}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
